<script setup>
/* eslint-disable */
import { ref, computed } from 'vue'
import { useGameStore } from '@/store/game'
import { CATCH_REGION_NAMES } from '@/constants/regionConstants'
import { ChocoboFactory } from '@/services/chocoboFactory'
import { CHOCOBO_BASE_STATS } from '@/constants/chocoboConstants'
import { Chocobo } from '@/models/chocobos'
import { getStatGrade, getStatGradeClass } from '@/utils/chocoboStats'
import { GameActions, GameActionTypes } from '@/utils/gameActions'
import { getChocoboColorClass } from '@/utils/colorUtils'
import { applyThemeClasses } from '@/utils/themeColors'

const emit = defineEmits(['close'])
const gameStore = useGameStore()

const selectedRegion = ref('region1')
const duration = ref(1)
const trackerType = ref('stable_staff')
const showResults = ref(false)
const expeditionResults = ref([])
const pendingChoices = ref([])

const EXPEDITION_COSTS = {
  stable_staff: 150,
  local_tracker: 3500,
  chocobo_sage: 10000
}

const REGION_COSTS = {
  base: 1000,
  different_region: 2500,
  different_continent: 15000
}

const CONTINENT_MAPPING = {
  eastern: ['region1', 'region2'],
  western: ['region3', 'region4'],
  mideel: ['region5'],
  northern: ['region6']
}

const TRACKER_OPTIONS = {
  stable_staff: {
    name: 'Stable Staff',
    description: '10% catch chance. Basic tracking abilities.',
    cost: EXPEDITION_COSTS.stable_staff
  },
  local_tracker: {
    name: 'Local Tracker',
    description: '30% catch chance. Expert in their home region.',
    cost: EXPEDITION_COSTS.local_tracker
  },
  chocobo_sage: {
    name: 'Chocobo Sage',
    description: '40% catch chance. Only brings back the best specimens.',
    cost: EXPEDITION_COSTS.chocobo_sage
  }
}

const isSameContinent = (region1, region2) => {
  return Object.values(CONTINENT_MAPPING).some(regions => 
    regions.includes(region1) && regions.includes(region2)
  )
}

const expeditionCost = computed(() => {
  let cost = EXPEDITION_COSTS[trackerType.value] * duration.value

  // Add region costs
  if (selectedRegion.value !== gameStore.playerOwner.homeRegion) {
    cost += REGION_COSTS.different_region
    
    // Add continent costs if applicable
    if (!isSameContinent(gameStore.playerOwner.homeRegion, selectedRegion.value)) {
      cost += REGION_COSTS.different_continent
    }
  }

  return cost
})

const canStartExpedition = computed(() => 
  gameStore.playerOwner.money >= expeditionCost.value &&
  gameStore.playerChocobos.length < gameStore.MAX_PLAYER_CHOCOBOS
)

const generateWildChocobo = (region, trackerType, playerHomeRegion) => {
  // Get all possible color/region combinations for the selected region
  const regionOptions = []
  
  for (const [color, regions] of Object.entries(CHOCOBO_BASE_STATS)) {
    if (regions[region]) {
      regionOptions.push({
        color,
        region,
        stats: regions[region]
      })
    }
  }
  
  if (regionOptions.length === 0) return null
  
  // Apply tracker type logic
  let selectedOption
  switch (trackerType) {
    case 'stable_staff':
      selectedOption = regionOptions[Math.floor(Math.random() * regionOptions.length)]
      break
      
    case 'local_tracker':
      if (region === playerHomeRegion) {
        // Sort by rarity and pick from top half
        const sorted = [...regionOptions].sort(
          (a, b) => b.stats.xfactor_percentage - a.stats.xfactor_percentage
        )
        selectedOption = sorted[Math.floor(Math.random() * Math.ceil(sorted.length / 2))]
      } else {
        // Random selection for non-home regions
        selectedOption = regionOptions[Math.floor(Math.random() * regionOptions.length)]
      }
      break
      
    case 'chocobo_sage':
      // Sort by rarity and pick from top third
      const sorted = [...regionOptions].sort(
        (a, b) => b.stats.xfactor_percentage - a.stats.xfactor_percentage
      )
      selectedOption = sorted[Math.floor(Math.random() * Math.ceil(sorted.length / 3))]
      break
  }

  if (!selectedOption) return null

  // Generate chocobo with selected properties
  const chocobo = new Chocobo(
    selectedOption.color,
    selectedOption.region,
    ChocoboFactory.generateRandomName()
  )

  return chocobo
}

const simulateExpedition = async () => {
  const results = []
  const catchRates = {
    stable_staff: 0.10,
    local_tracker: 0.30,
    chocobo_sage: 0.40
  }

  for (let day = 0; day < duration.value; day++) {
    const roll = Math.random()
    if (roll <= catchRates[trackerType.value]) {
      // Generate potential catch
      const wildChocobo = generateWildChocobo(
        selectedRegion.value,
        trackerType.value,
        gameStore.playerOwner.homeRegion
      )
      
      if (wildChocobo) {
        results.push({
          day: day + 1,
          success: true,
          chocobo: wildChocobo
        })
      }
    } else {
      results.push({
        day: day + 1,
        success: false
      })
    }
  }

  return results
}

const startExpedition = async () => {
  if (!canStartExpedition.value) return
  
  try {
    gameStore.playerOwner.money -= expeditionCost.value
    
    const results = await simulateExpedition()
    
    pendingChoices.value = results
      .filter(result => result.success && result.chocobo)
      .map(result => ({
        ...result,
        decided: false,
        accepted: false
      }))
    
    expeditionResults.value = results
    showResults.value = true

    // Pass duration to the action
    await GameActions.performAction(GameActionTypes.EXPEDITION, {
      duration: duration.value
    })
  } catch (error) {
    console.error('Expedition failed:', error)
  }
}

const acceptChocobo = (result) => {
  if (!result.decided) {
    result.decided = true
    result.accepted = true
    gameStore.chocobos.push(result.chocobo)
    gameStore.playerOwner.chocoboIds.push(result.chocobo.id)
  }
}

const rejectChocobo = (result) => {
  if (!result.decided) {
    result.decided = true
    result.accepted = false
  }
}

const allChoicesComplete = computed(() => 
  pendingChoices.value.every(result => result.decided)
)


</script>

<template>
  <div :class="`fixed inset-0 ${applyThemeClasses('components.modal.overlay')} flex items-center justify-center z-50`">
    <div :class="`rounded-lg ${applyThemeClasses('components.modal.container')} max-h-[90vh] overflow-y-auto p-6`">
      <div class="flex justify-between items-center mb-6">
        <h2 :class="applyThemeClasses('text.primary')">Chocobo Expedition</h2>
        <button 
          @click="$emit('close')"
          class="text-gray-500 hover:text-gray-700"
        >
          ✕
        </button>
      </div>

      <template v-if="!showResults">
        <div class="space-y-6">
          <!-- Region Selection -->
          <div>
            <label :class="applyThemeClasses('text.secondary')">
              Select Region
            </label>
            <select 
              v-model="selectedRegion"
              :class="`rounded-lg ${applyThemeClasses('form.select')}`"
            >
              <option 
                v-for="(name, id) in CATCH_REGION_NAMES" 
                :key="id" 
                :value="id"
              >
                {{ name }}
              </option>
            </select>
          </div>

          <!-- Duration Selection -->
          <div>
            <label :class="applyThemeClasses('text.secondaryBold')">
              Expedition Duration (Days)
            </label>
            <input 
              type="range"
              min="1"
              max="10"
              v-model="duration"
              class="w-full"
            />
            <div :class="`rounded-lg ${applyThemeClasses('form.input')}`">
              {{ duration }} {{ duration === 1 ? 'day' : 'days' }}
            </div>
          </div>

          <!-- Tracker Selection -->
          <div>
            <label :class="applyThemeClasses('text.secondary')">
              Choose Your Tracker
            </label>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div
                v-for="(option, id) in TRACKER_OPTIONS"
                :key="id"
                @click="trackerType = id"
                :class="[
                  'p-4 border rounded-lg cursor-pointer',
                  trackerType === id ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                ]"
              >
                <h3 :class="applyThemeClasses('text.secondaryBold')">{{ option.name }}</h3>
                <p :class="applyThemeClasses('text.secondary')">{{ option.description }}</p>
                <p class="text-sm font-medium text-green-600 mt-2">
                  {{ option.cost }}G per day
                </p>
              </div>
            </div>
          </div>

          <!-- Cost Summary -->
          <div :class="`rounded-lg shadow-lg ${applyThemeClasses('layout.card')}`">
            <h3 :class="applyThemeClasses('text.secondaryBold')">Expedition Summary</h3>
            <div class="space-y-1 text-sm">
              <p :class="applyThemeClasses('text.secondary')">Base Cost: {{ TRACKER_OPTIONS[trackerType].cost * duration }}G</p>
              <template v-if="selectedRegion !== gameStore.playerOwner.homeRegion">
                <p>Region Fee: {{ REGION_COSTS.different_region * duration }}G</p>
                <template v-if="!isSameContinent(gameStore.playerOwner.homeRegion, selectedRegion)">
                  <p :class="applyThemeClasses('text.secondaryBold')">Continent Fee: {{ REGION_COSTS.different_continent * duration }}G</p>
                </template>
              </template>
              <div class="border-t pt-2 mt-2">
                <p :class="applyThemeClasses('text.secondaryBold')">Total Cost: {{ expeditionCost }}G</p>
              </div>
            </div>
          </div>

          <!-- Start Button -->
          <div class="flex justify-end">
            <button
              @click="startExpedition"
              :disabled="!canStartExpedition"
              :class="[
                'px-6 py-2 rounded-lg text-white',
                canStartExpedition 
                  ? 'bg-green-500 hover:bg-green-600' 
                  : 'bg-gray-400 cursor-not-allowed'
              ]"
            >
              Start Expedition
            </button>
          </div>
        </div>
      </template>

      <!-- Results View -->
      <template v-if="showResults">
        <div class="space-y-6">
          <h3 class="text-xl font-semibold">Expedition Results</h3>
          
          <div class="space-y-4">
            <div 
              v-for="(result, index) in expeditionResults"
              :key="index"
              class="border rounded-lg p-4"
              :class="{
                'bg-gray-50': !result.success,
                'bg-green-50': result.success && pendingChoices.find(r => r.day === result.day)?.accepted,
                'bg-red-50': result.success && pendingChoices.find(r => r.day === result.day)?.decided && !pendingChoices.find(r => r.day === result.day)?.accepted
              }"
            >
              <div class="flex items-center justify-between">
                <h4 class="font-medium">Day {{ result.day }}</h4>
                <span 
                  :class="[
                    'px-2 py-1 rounded-full text-sm',
                    result.success 
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  ]"
                >
                  {{ result.success ? 'Caught!' : 'No catch' }}
                </span>
              </div>

              <template v-if="result.success && result.chocobo">
                <div class="mt-4">
                  <div class="flex items-center space-x-4">
                    <div 
                      :class="[getChocoboColorClass(result.chocobo.color), 'w-12 h-12 rounded-lg']"
                    ></div>
                    <div class="flex-grow">
                      <h5 class="font-semibold">{{ result.chocobo.name }}</h5>
                      <p class="text-sm text-gray-600">
                        {{ result.chocobo.color.charAt(0).toUpperCase() + result.chocobo.color.slice(1) }} Chocobo
                      </p>
                      
                      <!-- Stats Display -->
                      <div class="mt-2 flex space-x-4">
                        <span 
                          :class="[
                            getStatGradeClass(getStatGrade(result.chocobo.speed)),
                            'px-2 py-1 rounded text-sm font-medium'
                          ]"
                        >
                          Speed: {{ getStatGrade(result.chocobo.speed) }}
                        </span>
                        <span 
                          :class="[
                            getStatGradeClass(getStatGrade(result.chocobo.stamina)),
                            'px-2 py-1 rounded text-sm font-medium'
                          ]"
                        >
                          Stamina: {{ getStatGrade(result.chocobo.stamina) }}
                        </span>
                      </div>

                      <!-- Accept/Reject Buttons -->
                      <div 
                        v-if="pendingChoices.find(r => r.day === result.day && !r.decided)"
                        class="mt-4 flex space-x-4"
                      >
                        <button
                          @click="acceptChocobo(pendingChoices.find(r => r.day === result.day))"
                          class="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                          :disabled="!gameStore.canAddMoreChocobos"
                        >
                          Accept
                        </button>
                        <button
                          @click="rejectChocobo(pendingChoices.find(r => r.day === result.day))"
                          class="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                        >
                          Reject
                        </button>
                      </div>

                      <!-- Decision Display -->
                      <div 
                        v-else-if="pendingChoices.find(r => r.day === result.day)?.decided"
                        class="mt-4"
                      >
                        <span 
                          :class="[
                            'px-3 py-1 rounded-full text-sm',
                            pendingChoices.find(r => r.day === result.day)?.accepted
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          ]"
                        >
                          {{ pendingChoices.find(r => r.day === result.day)?.accepted ? 'Accepted' : 'Rejected' }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>

          <div class="flex justify-end">
            <button
              @click="$emit('close')"
              :disabled="!allChoicesComplete"
              :class="[
                'px-6 py-2 rounded-lg text-white',
                allChoicesComplete
                  ? 'bg-blue-500 hover:bg-blue-600'
                  : 'bg-gray-400 cursor-not-allowed'
              ]"
            >
              {{ allChoicesComplete ? 'Done' : 'Make all choices to continue' }}
            </button>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>